import { AlertTriangle } from 'lucide-react';
import type { LifecycleState } from '../../types/lifecycle';
import { isPreReady } from '../../types/lifecycle';

interface LifecycleBannerProps {
	state: LifecycleState | undefined;
	/** Callback to open the app's Settings -> Environment tab. */
	onConfigure: () => void;
}

const COPY: Partial<Record<LifecycleState, { title: string; cta: string }>> = {
	NEEDS_CONFIG: {
		title: 'This app needs configuration before it can run.',
		cta: 'Configure now',
	},
	INSTALLED: {
		title: 'This app is installed but not yet deployed.',
		cta: 'Configure now',
	},
	DEPLOYING: {
		title: 'This app is deploying — give it a moment.',
		cta: 'View settings',
	},
	DEGRADED: {
		title: 'This app is degraded. Check configuration or service health.',
		cta: 'Open settings',
	},
	UNINSTALLING: {
		title: 'This app is being uninstalled.',
		cta: 'Open settings',
	},
};

const TONE: Partial<Record<LifecycleState, { bg: string; fg: string; border: string }>> = {
	NEEDS_CONFIG: { bg: 'rgba(234,179,8,0.12)', fg: 'var(--khal-status-warning)', border: 'rgba(234,179,8,0.4)' },
	INSTALLED: { bg: 'rgba(234,179,8,0.12)', fg: 'var(--khal-status-warning)', border: 'rgba(234,179,8,0.4)' },
	DEPLOYING: { bg: 'rgba(59,130,246,0.12)', fg: 'var(--khal-accent-blue)', border: 'rgba(59,130,246,0.4)' },
	DEGRADED: { bg: 'rgba(239,68,68,0.12)', fg: 'var(--khal-status-error)', border: 'rgba(239,68,68,0.4)' },
	UNINSTALLING: { bg: 'rgba(107,114,128,0.12)', fg: 'var(--khal-text-muted)', border: 'rgba(107,114,128,0.4)' },
};

/**
 * Top-of-window banner shown when the app is not yet RUNNING. Wrap your
 * app content with this; the banner only renders when the state warrants it.
 *
 *   <LifecycleBanner state={state} onConfigure={openSettings}>
 *     <YourAppShell />
 *   </LifecycleBanner>
 */
export function LifecycleBanner({
	state,
	onConfigure,
	children,
}: React.PropsWithChildren<LifecycleBannerProps>) {
	if (!isPreReady(state)) return <>{children}</>;
	const copy = COPY[state!];
	const tone = TONE[state!];
	if (!copy || !tone) return <>{children}</>;

	return (
		<div className="flex h-full flex-col">
			<div
				role="status"
				data-testid={`lifecycle-banner-${state}`}
				className="flex shrink-0 items-center justify-between gap-3 px-3"
				style={{
					height: 36,
					background: tone.bg,
					color: tone.fg,
					borderBottom: `1px solid ${tone.border}`,
					fontSize: 12,
				}}
			>
				<span className="flex items-center gap-2">
					<AlertTriangle size={14} aria-hidden="true" />
					<span>{copy.title}</span>
				</span>
				<button
					type="button"
					className="rounded px-2 py-0.5 text-[11px] font-semibold transition-colors"
					style={{
						background: tone.fg,
						color: 'var(--khal-bg)',
					}}
					onClick={onConfigure}
				>
					{copy.cta}
				</button>
			</div>
			<div className="flex-1 overflow-hidden">{children}</div>
		</div>
	);
}
